import emoji from 'emoji-dictionary';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import htmlParser from 'react-markdown/plugins/html-parser';
import ReactMarkdown from 'react-markdown/with-html';
import removeMd from 'remove-markdown';

import AsideLearn from '@components/AsideLearn';
import CodeBlock from '@components/CodeBlock';
import ContainerSidebar from '@components/ContainerSidebar';
import CustomHead from '@components/CustomHead';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import HeadingRenderer from '@components/HeadingRenderer';
import { Arrow } from '@components/Icons';
import styles from '@css/markdown.module.css';
import { getSqlTutorials } from '@utils/contentfulClient';

const FooterNavigation = ({ dbName, tutorials, currentSlug }) => {
  const filteredTutorials = tutorials.filter((t) =>
    t.fields.category.includes(dbName),
  );
  const currentIndex = filteredTutorials
    .map((t) => t.fields.slug)
    .indexOf(currentSlug);
  const prevTutorial =
    currentIndex === 0 ? null : filteredTutorials[currentIndex - 1];
  const nextTutorial =
    currentIndex === filteredTutorials.length
      ? null
      : filteredTutorials[currentIndex + 1];

  return (
    <div className="flex justify-between gap-6 mt-20 mb-10 md:my-20">
      <div>
        {prevTutorial && (
          <>
            <p className="text-sm text-gray-500 mb-2">Previous</p>
            <Link href={prevTutorial.fields.slug}>
              <a className="flex flex-no-wrap items-center text-blue-100 hover:text-blue-200 font-medium transition-colors duration-200 ease-in-out">
                <Arrow color="currentColor" className="mr-2" />{' '}
                {prevTutorial.fields.title}
              </a>
            </Link>
          </>
        )}
      </div>
      <div>
        {nextTutorial && (
          <>
            <p className="text-sm text-gray-500 mb-2">Next</p>
            <Link href={nextTutorial.fields.slug}>
              <a className="flex flex-no-wrap items-center justify-end text-blue-100 hover:text-blue-200 font-medium transition-colors duration-200 ease-in-out">
                {nextTutorial.fields.title}
                <Arrow
                  color="currentColor"
                  className="ml-4 transform rotate-180"
                />
              </a>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

const SqlTutorialPage = ({ listOfTutorials, foundTutorial }) => {
  const router = useRouter();
  const tutorial = foundTutorial[0].fields;

  const getSummary = (source) => {
    const stripped = removeMd(source, {
      stripListLeaders: true,
      listUnicodeChar: '',
      gfm: true,
    });
    const lines = stripped.split('\n');

    // print the first few lines...
    return `${lines[0]} ${lines[1]} ${lines[2]} ${lines[3]}`;
  };

  const cleanPost = {
    metaTags: tutorial.metaTags?.fields,
    title: tutorial.title,
    body: tutorial.body,
    summary: getSummary(tutorial.body),
  };

  const dbName =
    tutorial.category[0] === 'PostgresSQL'
      ? 'PostgreSQL'
      : tutorial.category[0];
  const pageTitle = `${cleanPost.title} in ${dbName}`;
  const metaTitle = cleanPost.metaTags?.title || `${cleanPost.title} - PopSQL`;
  const metaDesc = cleanPost.metaTags?.description || cleanPost.summary;

  const emojiSupport = (text) =>
    text.value.replace(/:\w+:/gi, (name) => emoji.getUnicode(name));

  const parseHtml = htmlParser({
    isValidNode: (node) => node.type !== 'script',
  });

  const Sidebar = <AsideLearn data={listOfTutorials} />;

  return (
    <>
      <CustomHead
        meta={{
          title: metaTitle,
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/learn.png`,
          description: metaDesc,
        }}
        openGraph={{
          title: metaTitle,
          description: metaDesc,
          url: `${process.env.NEXT_PUBLIC_WEB_HOST}${router.asPath}`,
        }}
      />
      <main>
        <Header />
        <ContainerSidebar
          type="learn"
          postTitle={pageTitle}
          asideComponent={Sidebar}
          dbName={dbName}
        >
          <div className="max-w-[740px] relative">
            <div>
              <ReactMarkdown
                className={styles.popsql__markdown}
                source={cleanPost.body}
                escapeHtml={false}
                renderers={{
                  code: CodeBlock,
                  heading: HeadingRenderer,
                  text: emojiSupport,
                }}
                astPlugins={[parseHtml]}
              />
              <FooterNavigation
                dbName={tutorial.category[0]}
                tutorials={listOfTutorials}
                currentSlug={tutorial.slug}
              />
            </div>
          </div>
        </ContainerSidebar>
      </main>
      <Footer />
    </>
  );
};

export async function getStaticPaths() {
  const tutorials = await getSqlTutorials();
  const paths = tutorials.map((t) => t.fields.slug);

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps(ctx) {
  const { id, slug } = ctx.params;
  const tutorials = await getSqlTutorials();
  const foundTutorial = tutorials.filter(
    (t) => t.fields.slug === `/learn-sql/${id}/${slug}`,
  );

  return {
    props: {
      listOfTutorials: tutorials,
      foundTutorial,
    },
  };
}

export default SqlTutorialPage;
